import { VOCAB } from '../vocab.js'

export default {
	ids: ['angle', 'count', 'width', 'spacing', 'cross', 'twist', 'unwind', 'speed', 'grow'],
	glsl: `
// Cells of travel the blob field hands the lattice ahead of speed's (displacement's blobsAt), 0 without blobs.
float fieldLead = 0.0;

// Count's top stop: all, no window.
const float COUNT_ALL = ${VOCAB.count.maximum}.0;

// The lines count c shows: c itself up to the stop below all, then growing without bound as c reaches it, so a sweep into all adds lines one at a time and never jumps.
float countLines(float c) {
	return c < COUNT_ALL - 1.0 ? c : (COUNT_ALL - 1.0) / max(COUNT_ALL - c, 1e-6);
}

// Count's window on the lines' fixed places, in pitches from the surface's centre line (enclose 0) or the outline's line (enclose 1): [x, y], n pitches long. It grows alternately at the back and the front, so a whole count sits centred within half a pitch and no line moves; round the outline it grows inward alone.
vec2 countWindow(float n) {
	float m = floor(n);
	float front = (floor(0.5 * (m - 1.0)) + (mod(m, 2.0) < 0.5 ? n - m : 0.0)) * (1.0 - uEnclose);
	return vec2(-0.5 - front, n - 0.5 - front);
}

// A line's share of a window: its cell's overlap with it, so a line at an edge is as wide as the part of its cell inside.
float countShare(float place, vec2 w) {
	return clamp(min(w.y, place + 0.5) - max(w.x, place - 0.5), 0.0, 1.0);
}

// Grow: a line's width × 2^grow per GROW_SU su it travels (toward smaller x, where the lines go), spacing held.
const float GROW_SU = 100.0;

// Grow's rate per su.
float growRate() {
	return uGrow * 0.6931471805599453 / GROW_SU;
}

// The exponent's clamp, beyond which lines are far under or over a pixel.
const float GROW_CLAMP = 40.0;

// The cells from the anchor to X su on when the width at X is a·e^(−gm·X) and the gap b: ∫ dX / (a·e^(−gm·X) + b) = ln(1 + b(e^(gm·X) − 1)/(a + b)) / (gm·b), in forms exact to float precision as gm·X or b goes to 0 (X / (a + b) at gm 0).
float growCell(float X, float a, float b, float gm) {
	float y = clamp(gm * X, -GROW_CLAMP, GROW_CLAMP);
	float e1 = abs(y) < 1e-3 ? 1.0 + y * (0.5 + y / 6.0) : (exp(y) - 1.0) / y;
	float z = b * y * e1 / (a + b);
	float l1 = abs(z) < 1e-3 ? 1.0 - z * (0.5 - z / 3.0) : log(1.0 + z) / z;
	return (y / gm) * e1 * l1 / (a + b);
}

// idx: the band (x) and cross band (y) through the point; cov: each family's coverage; band: the coverage painted, blurred to the average past a pixel per cell; blur: that blend; turns: the cells a carried travel folded away; local: the position in the cell, -0.5..0.5.
struct Cell {
	vec2 idx;
	vec2 cov;
	float band;
	float blur;
	float turns;
	vec2 local;
};

// The arm round the wall: f the phase in winds (travel included), turn the angle sheared by unwind (rings at 1), aa the pattern's change per pixel; past a pixel per wind the band blends to its average cover instead of moiréing.
float armsCover(float f, float turn, float edge, float aa) {
	return 1.0 - smoothstep(edge - aa, edge + aa, abs(fract(f + turn) - 0.5));
}

// Count round the tube, at D winds down the depth from its outer end (depth 0, the flat rings' outline): rings (unwind 1) show whole, the next growing in width (place: its centre's D); the arm (unwind 0), one line, is cut at D = n, so it grows along its length. Between, the shear's pieces each keep one width. All shows everything.
float tubeCount(float edge, float place, float D, float aa) {
	if (uCount >= COUNT_ALL) return edge;
	float n = countLines(uCount);
	float ring = edge * countShare(place, vec2(-0.5, n - 0.5));
	float arm = edge * (1.0 - smoothstep(-aa, aa, D + 0.5 - n));
	return mix(arm, ring, uUnwind);
}

// The palette's period in index at a spread (its two whole counts, mirrored or not).
float spanAt(float spread) {
	float n = floor(max(spread, 2.0));
	return uRepeat > 0.5 ? 2.0 * n * (n - 1.0) : n * (n + 1.0);
}

// A path between two spreads (repeatPalette) repeats over both ends' periods, fixed while spread tweens, so carried turns never step at a whole count.
float paletteSpan() {
	if (uSpreadFrom > 0.0 && uSpreadTo != uSpreadFrom) return spanAt(uSpreadFrom) * spanAt(uSpreadTo);
	return spanAt(uSpread);
}

// The palette's least period in index at a whole spread; ramp and mirror both while repeat is between.
float periodAt(float spread) {
	float n = floor(max(spread, 2.0));
	float mirror = 2.0 * (n - 1.0);
	return uRepeat <= 0.0 ? n : uRepeat >= 1.0 ? mirror : n * mirror;
}

float palettePeriod() {
	if (uSpreadFrom > 0.0 && uSpreadTo != uSpreadFrom) return periodAt(uSpreadFrom) * periodAt(uSpreadTo);
	return periodAt(uSpread);
}

// The pattern coordinate of the frontmost covering strip, else the ground cell: line (k + across, travel included: a line's index keeps as it moves), along it (su), the waveform's phase (turns) and whether the pixel is on the pattern. Every branch writes it; pick reads it.
struct Pattern {
	float line;
	float along;
	float phase;
	float hit;
};
Pattern pat;

// Tunnel > 0: one arm round the wall down the depth (fog darkens the mouth to the ground); + speed travels it outward, as the flat rings. The depth is in pitches of s.axial (su down the wall, isometric with the plane), scaled by twist in the arm's share alone (rings ignore it); the displacement (hc, cells) moves it as on the plane. Unwind shears the arm by its share of a turn per turn, so at 1 it is rings (every subset's default, spiral's 0), torn between on the cut ray (the top, s.turn's seam): the path operator between them. Each line is one palette entry: ring k entry k, the arm one entry, the shear's pieces k × unwind (carried turns likewise), so no line changes colour along it. Below tunnel 1 the surface is the hinge (surface()), whose arc down the wall is the flat rings' distance to the square section's outline at 0; the flat bands' own (xp: fit, rounding's superellipse, zoom's lattice scale, tilt) blends into it over the first HINGE_BLEND of tunnel, both growing inward, so no ring folds; cover and palette are drawn once from it, the cross family fading out.
// The tube's lines thin quadratically with depth: they keep their own cover and colour down to 2 px wide (cells per pixel 0.5), averaged by 1 px.
const vec2 TUBE_BLUR = vec2(0.5, 1.0);
const float HINGE_BLEND = 0.25;

// flatEdge: the flat rings' own edge (grown), handing over to the tube's by w.
void tube(inout Cell c, Surf s, float size, float edge, float flatEdge, float xp, float aaY, float travel, float hc) {
	float twist = mix(uTwist, 1.0, uUnwind);
	float depth = s.axial * twist / size + hc;
	pat.along = s.turn * TAU * TUBE_RADIUS;
	pat.phase = 0.0;
	float w = uTunnel < 1.0 ? smoothstep(0.0, 1.0, uTunnel) : 1.0;
	float turn = s.turn * (1.0 - uUnwind);
	float dTurn = s.dTurn * (1.0 - uUnwind);
	float P = palettePeriod();
	float turns = uUnwind * (uSpeedTurns - P * floor(uSpeedTurns / P));
	if (uTunnel >= 1.0) {
		float dT = uBend > 0.0 || vxH > 0.0 ? s.dDepth * abs(twist) / size : fwidth(depth);
		float f = depth + travel;
		float k = floor(f + turn);
		float e = tubeCount(edge, k - travel, depth - 0.5, dT);
		float aa = dT + dTurn;
		c.band = mix(armsCover(f, turn, e, aa), 2.0 * e, smoothstep(TUBE_BLUR.x, TUBE_BLUR.y, aa));
		c.idx.x = k * uUnwind;
		c.turns = turns;
		c.blur = smoothstep(TUBE_BLUR.x, TUBE_BLUR.y, dT / max(uSpread - 1.0, 1.0));
		c.cov.y = 0.0;
		pat.line = f + turn - 0.5;
		return;
	}
	// The phase, negated: armsCover is symmetric in it. The hinge's lines sit at whole cells on the flat plane, half cells on the tube.
	float f = -mix(xp + 0.5, depth + 0.5 * (1.0 - uTunnel), smoothstep(0.0, HINGE_BLEND, uTunnel)) - travel;
	pat.line = turn - f - 0.5;
	float dF = fwidth(f);
	float k = floor(turn - f);
	float e = tubeCount(mix(flatEdge, edge, w), k - travel, -f - 0.5 - travel, dF);
	float cy = c.cov.y * (1.0 - w);
	float aaC = aaY * (1.0 - w);
	float fill = 1.0 - (1.0 - 2.0 * e) * (1.0 - 2.0 * edge * uCross * (1.0 - w));
	float aaA = dF + dTurn;
	float x0 = armsCover(f, -turn, e, aaA);
	c.cov = vec2(x0, cy);
	vec2 blur = mix(vec2(0.1, 0.4), TUBE_BLUR, w);
	c.band = mix(max(x0, cy), fill, smoothstep(blur.x, blur.y, max(aaA, aaC)));
	c.idx.x = k * uUnwind;
	c.turns = turns;
	c.blur = mix(c.blur, smoothstep(TUBE_BLUR.x, TUBE_BLUR.y, dF / max(uSpread - 1.0, 1.0)), w);
}

// h displaces across the bands (as lying rows). Speed travels the cells across the lines, already in cells; the palette rides its folds (uSpeedTurns). Round the tube, tube().
Cell lattice(Surf s, float h) {
	float size = max(uWidth + uSpacing, 1e-4);
	// Across the plane the displacement bends each side before the fold meets them; round the outline, the folded ring before the rim.
	float x = uEnclose < 1e-4 ? min(s.near + h, s.mirror + h + s.hold) : min(s.near, s.mirror + s.hold) + h + s.rim;
	float edge = 0.5 * uWidth / size;
	float gm = growRate();
	float travel = uSpeed + fieldLead;
	vec2 cell = vec2((x - s.anchor) / (size * s.unit) + travel, 0.0);
	// Grown, the lines' edge at the pixel's own width; the cross family keeps the plain one.
	float edgeX = edge;
	if (gm != 0.0) {
		float X = (x - s.anchor) / s.unit;
		cell.x = growCell(X, uWidth, uSpacing, gm) + travel;
		float w = uWidth * exp(-clamp(gm * X, -GROW_CLAMP, GROW_CLAMP));
		edgeX = 0.5 * w / max(w + uSpacing, 1e-4);
	}
	Cell c;
	c.turns = uSpeedTurns;
	c.idx = vec2(floor(cell.x + 0.5), 0.0);
	c.local = vec2(fract(cell.x + 0.5) - 0.5, 0.0);
	// Count's window, the rows' own: each band as wide as its cell's share of it.
	float xEdge = edgeX;
	if (uCount < COUNT_ALL) xEdge *= countShare(c.idx.x - travel, countWindow(countLines(uCount)));
	vec2 inCell = vec2(abs(fract(cell.x - 0.5) - 0.5), 0.5);
	vec2 aa = vec2(fwidth(cell.x), 0.0);
	c.cov = vec2(1.0 - smoothstep(xEdge - aa.x, xEdge + aa.x, inCell.x), 0.0);
	float fill = xEdge * 2.0;
	if (uCross > 0.0) {
		cell.y = s.l / (size * s.unit);
		c.idx.y = floor(cell.y + 0.5);
		c.local.y = fract(cell.y + 0.5) - 0.5;
		inCell.y = abs(fract(cell.y - 0.5) - 0.5);
		aa.y = fwidth(cell.y);
		c.cov.y = uCross * (1.0 - smoothstep(edge - aa.y, edge + aa.y, inCell.y));
		fill = 1.0 - (1.0 - 2.0 * xEdge) * (1.0 - 2.0 * edge * uCross);
	}
	c.band = max(c.cov.x, c.cov.y);
	c.blur = smoothstep(0.1, 0.4, max(aa.x, aa.y * uCross));
	c.band = mix(c.band, fill, c.blur);
	pat.line = cell.x;
	pat.along = s.l / s.unit;
	pat.phase = s.l / (uWaveLength * s.unit) - uWaveSpeed;
	pat.hit = step(0.999, s.hit);
	if (uTunnel > 0.0) tube(c, s, size, edge, edgeX, cell.x - travel, aa.y * uCross, travel, h / (size * s.unit));
	return c;
}

// Rows (light's rows 1): count's most, plus the rows growing in and fading out at the window's edges.
const int MAX_ROWS = ${VOCAB.count.maximum + 4};
`,
}
